
import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useParams } from "react-router-dom";
import * as powerbi from "powerbi-client";
import { getGroupToRoles, getRoleToPageAccess } from "../config/apiService";

// Instantiate Power BI embed service
const embedService = new powerbi.service.Service(
  powerbi.factories.hpmFactory,
  powerbi.factories.wpmpFactory,
  powerbi.factories.routerFactory
);

function slugify(str) {
  return decodeURIComponent(str)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/&/g, "and")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/--+/g, "-")
    .trim();
}

export default function PowerBIEmbed() {
  const { accounts } = useMsal();
  const account = accounts[0];
  const { dashboard, report, page } = useParams();
  const containerRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {  
    if (!account) return;

    async function fetchAndEmbed() {
      try {
        const username = account.username;
        const userGroupIds = account.idTokenClaims?.groups || [];

        // 1) Fetch pages data from backend
        const backendPages = await getRoleToPageAccess();
        const groupRolesData = await getGroupToRoles();

        // 2) Find the page object matching the route
        const pageObj = backendPages.find(
          (p) =>
            slugify(p.dashboard) === dashboard &&
            slugify(p.report) === report &&
            slugify(p.page_name) === page
        );

        if (!pageObj) {
          setError(`No matching page '${page}' under report '${report}'`);
          return;
        }

        const { groupId, reportId, datasetId, sectionId, rlsEnabled } = pageObj;

        // 3) Convert backend Role to roles array (only if RLS enabled)
        const effectiveRoles = groupRolesData
          .filter((role) => userGroupIds.includes(role.GroupId))
          .map((role) => role.Group_Name); // map to role names


        // 4) Build embed token request
        const body = {
          groupId,
          reportId,
          datasetId,
          username,
          rlsEnabled,
          ...(rlsEnabled ? { roles: effectiveRoles } : {}),
        };

        console.log("→ RLS token request body:", body);
        console.log("Embed token request body:", body);
        console.log("##### FRONTEND DEBUG ######");
        console.log("groupId:", groupId);
        console.log("reportId:", reportId);
        console.log("datasetId:", datasetId);
        console.log("username:", username);
        console.log("roles:", rlsEnabled ? effectiveRoles : []);
        console.log("Sending body:", body);


        // 5) Fetch embed token
        const res = await fetch("/api/powerbi/getEmbedToken", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        const data = await res.json();

        const { embedUrl, token } = data;
        if (!embedUrl || !token) throw new Error("Missing embedUrl or token");

        // 6) Embed the report
        const config = {
          type: "report",
          tokenType: powerbi.models.TokenType.Embed,
          accessToken: token,
          embedUrl,
          id: reportId,
          pageName: sectionId,
          settings: { filterPaneEnabled: false, navContentPaneEnabled: false },
        };

        if (containerRef.current) {
          embedService.reset(containerRef.current);
          embedService.embed(containerRef.current, config);
        }
      } catch (err) {
        console.error("Failed to fetch/embed token:", err);
        setError("Embed token generation failed");
      }
    }

    fetchAndEmbed();
  }, [account, dashboard, report, page]);

  if (error) return <div style={{ color: "red", padding: 20 }}>{error}</div>;

  return <div ref={containerRef} style={{ width: "100%", height: "100%" }} />;
}

